/**
 * Planar geometry for roof/land surfaces and module auto-placement.
 * Coordinates are local metric (x east, y south), derived from Web-Mercator
 * projection of the site with latitude correction applied upstream.
 */
import type {
  LayoutOptions,
  LayoutResult,
  Obstacle,
  PlacedModule,
  Point,
  Polygon,
  PvModuleSpec,
  Surface,
} from "./types.js";

export function polygonArea(poly: Polygon): number {
  const v = poly.vertices;
  if (v.length < 3) return 0;
  let a = 0;
  for (let i = 0; i < v.length; i++) {
    const p = v[i]!;
    const q = v[(i + 1) % v.length]!;
    a += p.x * q.y - q.x * p.y;
  }
  return Math.abs(a / 2);
}

export function centroid(poly: Polygon): Point {
  const v = poly.vertices;
  let cx = 0;
  let cy = 0;
  let a = 0;
  for (let i = 0; i < v.length; i++) {
    const p = v[i]!;
    const q = v[(i + 1) % v.length]!;
    const cross = p.x * q.y - q.x * p.y;
    a += cross;
    cx += (p.x + q.x) * cross;
    cy += (p.y + q.y) * cross;
  }
  a /= 2;
  if (Math.abs(a) < 1e-9) {
    const n = v.length || 1;
    return {
      x: v.reduce((s, p) => s + p.x, 0) / n,
      y: v.reduce((s, p) => s + p.y, 0) / n,
    };
  }
  return { x: cx / (6 * a), y: cy / (6 * a) };
}

export function pointInPolygon(pt: Point, poly: Polygon): boolean {
  const v = poly.vertices;
  let inside = false;
  for (let i = 0, j = v.length - 1; i < v.length; j = i++) {
    const pi = v[i]!;
    const pj = v[j]!;
    if (
      pi.y > pt.y !== pj.y > pt.y &&
      pt.x < ((pj.x - pi.x) * (pt.y - pi.y)) / (pj.y - pi.y) + pi.x
    ) {
      inside = !inside;
    }
  }
  return inside;
}

export function distancePointToSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

export function distanceToBoundary(pt: Point, poly: Polygon): number {
  const v = poly.vertices;
  let min = Infinity;
  for (let i = 0; i < v.length; i++) {
    min = Math.min(min, distancePointToSegment(pt, v[i]!, v[(i + 1) % v.length]!));
  }
  return min;
}

/**
 * True when the rectangle lies fully inside the polygon respecting an inner
 * setback: all corners + midpoints inside AND at least `setback` from every edge.
 */
export function rectInsideWithSetback(
  x: number,
  y: number,
  w: number,
  h: number,
  poly: Polygon,
  setbackM: number,
): boolean {
  const probes: Point[] = [
    { x, y },
    { x: x + w, y },
    { x: x + w, y: y + h },
    { x, y: y + h },
    { x: x + w / 2, y },
    { x: x + w / 2, y: y + h },
    { x, y: y + h / 2 },
    { x: x + w, y: y + h / 2 },
    { x: x + w / 2, y: y + h / 2 },
  ];
  for (const p of probes) {
    if (!pointInPolygon(p, poly)) return false;
  }
  if (setbackM > 0) {
    for (const p of probes) {
      if (distanceToBoundary(p, poly) < setbackM - 1e-9) return false;
    }
  }
  return true;
}

export function rectIntersectsObstacle(
  x: number,
  y: number,
  w: number,
  h: number,
  o: Obstacle,
  bufferM: number,
): boolean {
  const cx = Math.max(x, Math.min(o.center.x, x + w));
  const cy = Math.max(y, Math.min(o.center.y, y + h));
  return Math.hypot(o.center.x - cx, o.center.y - cy) < o.radiusM + bufferM;
}

/**
 * Static obstacle shading factor for a module (1 = unshaded).
 * Obstacles cast a shadow band to the north (negative y) whose length scales
 * with obstacle height (~2.5×h covers winter sun at German latitudes).
 */
export function obstacleShadeFactor(
  m: { x: number; y: number; w: number; h: number },
  obstacles: Obstacle[],
): number {
  let f = 1;
  const cx = m.x + m.w / 2;
  const cy = m.y + m.h / 2;
  for (const o of obstacles) {
    const dx = cx - o.center.x;
    const dy = cy - o.center.y; // negative dy = module is north of obstacle
    const shadowLen = Math.max(1.5, o.heightM * 2.5);
    if (dy < 0 && Math.abs(dy) < shadowLen && Math.abs(dx) < o.radiusM + 0.8) {
      const depth = 1 - Math.abs(dy) / shadowLen; // deeper shadow closer to obstacle
      f = Math.min(f, 1 - 0.35 * depth - 0.05);
    } else if (Math.hypot(dx, dy) < o.radiusM * 2.2) {
      f = Math.min(f, 0.93);
    }
  }
  return Math.max(0.5, f);
}

/** Rotate a point around a pivot by `deg` degrees (clockwise, screen coords). */
export function rotatePoint(p: Point, pivot: Point, deg: number): Point {
  const r = (deg * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  const dx = p.x - pivot.x;
  const dy = p.y - pivot.y;
  return { x: pivot.x + dx * cos - dy * sin, y: pivot.y + dx * sin + dy * cos };
}

export interface AutoLayoutOverrides {
  /** Keys removed by the planner. */
  removedKeys?: ReadonlySet<string> | string[];
  /** Manual per-module offsets in GRID frame, keyed by module key. */
  movedOffsets?: Record<string, { dx: number; dy: number }>;
}

/**
 * Auto-placement of modules on a surface.
 * Grid fill with orientation, inter-module gap, racking row pitch, optional
 * maintenance walkways, edge setback, obstacle exclusion buffers,
 * grid rotation (roof-ridge alignment) and manual move/remove overrides.
 * Module coordinates are returned in the GRID frame together with the
 * rotation + pivot needed to map them into world coordinates.
 */
export function autoLayout(
  surface: Surface,
  module: PvModuleSpec,
  opts: LayoutOptions,
  overrides: AutoLayoutOverrides = {},
): LayoutResult {
  const rotation = surface.gridRotationDeg ?? 0;
  const worldPoly = surface.boundary;
  const pivot = worldPoly.vertices.length >= 3 ? centroid(worldPoly) : { x: 0, y: 0 };
  const toGrid = (p: Point): Point => rotatePoint(p, pivot, -rotation);
  const toWorld = (p: Point): Point => rotatePoint(p, pivot, rotation);
  const poly: Polygon =
    rotation === 0 ? worldPoly : { vertices: worldPoly.vertices.map(toGrid) };
  const gridObstacles: Obstacle[] =
    rotation === 0
      ? surface.obstacles
      : surface.obstacles.map((o) => ({ ...o, center: toGrid(o.center) }));
  const surfaceArea = polygonArea(poly);
  const modules: PlacedModule[] = [];

  if (poly.vertices.length >= 3) {
    const mw = opts.orientation === "portrait" ? module.widthM : module.heightM;
    const mh = opts.orientation === "portrait" ? module.heightM : module.widthM;
    const gap = Math.max(0, opts.moduleGapM);
    const rowPitchExtra =
      surface.kind === "pitched_roof" ? 0 : Math.max(0, opts.rowGapM);

    const xs = poly.vertices.map((p) => p.x);
    const ys = poly.vertices.map((p) => p.y);
    const x0 = Math.min(...xs);
    const x1 = Math.max(...xs);
    const y0 = Math.min(...ys);
    const y1 = Math.max(...ys);

    const walkEvery = opts.walkwayEveryRows ?? 0;
    const walkW = opts.walkwayWidthM ?? 0;

    let row = 0;
    let y = y0;
    while (y + mh <= y1 + 1e-9) {
      let col = 0;
      for (let x = x0; x + mw <= x1 + 1e-9; x += mw + gap) {
        const inside = rectInsideWithSetback(
          x,
          y,
          mw,
          mh,
          poly,
          surface.edgeSetbackM,
        );
        if (!inside) {
          col++;
          continue;
        }
        const blocked = gridObstacles.some((o) =>
          rectIntersectsObstacle(x, y, mw, mh, o, surface.obstacleBufferM),
        );
        if (blocked) {
          col++;
          continue;
        }
        const wc = toWorld({ x: x + mw / 2, y: y + mh / 2 });
        modules.push({
          key: `${row}_${col}`,
          x,
          y,
          w: mw,
          h: mh,
          row,
          col,
          shadeFactor: obstacleShadeFactor(
            { x: wc.x - mw / 2, y: wc.y - mh / 2, w: mw, h: mh },
            surface.obstacles,
          ),
        });
        col++;
      }
      row++;
      y += mh + gap + rowPitchExtra;
      if (walkEvery > 0 && walkW > 0 && row % walkEvery === 0) {
        y += walkW;
      }
    }
  }

  // Manual overrides: removals, then per-module moves with validity check.
  const removed = overrides.removedKeys
    ? overrides.removedKeys instanceof Set
      ? overrides.removedKeys
      : new Set(overrides.removedKeys)
    : null;
  let placed = removed ? modules.filter((m) => !removed.has(m.key)) : modules;

  const invalidMovedKeys: string[] = [];
  const moves = overrides.movedOffsets;
  if (moves && Object.keys(moves).length > 0) {
    placed = placed.map((m) => {
      const mv = moves[m.key];
      if (!mv) return m;
      const x = m.x + mv.dx;
      const y = m.y + mv.dy;
      const inside = rectInsideWithSetback(x, y, m.w, m.h, poly, surface.edgeSetbackM);
      const hitsObstacle = gridObstacles.some((o) =>
        rectIntersectsObstacle(x, y, m.w, m.h, o, surface.obstacleBufferM),
      );
      const overlaps = placed.some(
        (other) =>
          other.key !== m.key &&
          x < other.x + other.w &&
          x + m.w > other.x &&
          y < other.y + other.h &&
          y + m.h > other.y,
      );
      const invalid = !inside || hitsObstacle || overlaps;
      if (invalid) invalidMovedKeys.push(m.key);
      const wc = toWorld({ x: x + m.w / 2, y: y + m.h / 2 });
      return {
        ...m,
        x,
        y,
        invalid,
        shadeFactor: obstacleShadeFactor(
          { x: wc.x - m.w / 2, y: wc.y - m.h / 2, w: m.w, h: m.h },
          surface.obstacles,
        ),
      };
    });
  }

  const moduleArea = placed.length * module.widthM * module.heightM;
  const avgShade =
    placed.length > 0
      ? placed.reduce((s, m) => s + m.shadeFactor, 0) / placed.length
      : 1;

  return {
    modules: placed,
    moduleCount: placed.length,
    dcKwp: (placed.length * module.wp) / 1000,
    moduleAreaM2: moduleArea,
    surfaceAreaM2: surfaceArea,
    usableAreaM2: Math.max(0, surfaceArea), // refined by setback in UI metrics
    groundCoverageRatio: surfaceArea > 0 ? moduleArea / surfaceArea : 0,
    avgShadeFactor: avgShade,
    gridRotationDeg: rotation,
    gridPivot: pivot,
    invalidMovedKeys,
  };
}

/**
 * Minimum row pitch for tilted racking that avoids row-to-row shading at a
 * given design sun elevation (typically winter solstice noon ≈ 15° in Germany).
 */
export function minRowPitch(
  moduleSlantLengthM: number,
  rackTiltDeg: number,
  designSunElevationDeg = 15,
): number {
  const tilt = (rackTiltDeg * Math.PI) / 180;
  const sunEl = (designSunElevationDeg * Math.PI) / 180;
  const h = moduleSlantLengthM * Math.sin(tilt);
  const base = moduleSlantLengthM * Math.cos(tilt);
  return base + h / Math.tan(sunEl);
}
